import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@structural-climbing/ui'
import type { ProductCategory } from './types'

interface Props {
  isOpen: boolean
  /** Null creates a category. */
  editTarget: ProductCategory | null
  onClose: () => void
  onSaved: () => void
}

export function CategoryFormModal({ isOpen, editTarget, onClose, onSaved }: Props) {
  const { t } = useTranslation()
  const { getAccessTokenSilently } = useAuth()
  const apiUrl = import.meta.env.VITE_API_URL as string

  const [name, setName] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen) return
    setName(editTarget?.name ?? '')
    setError(null)
  }, [isOpen, editTarget])

  async function handleSave() {
    if (!name.trim()) {
      setError(t('products.nameRequired'))
      return
    }
    setSaving(true)
    setError(null)
    try {
      const token = await getAccessTokenSilently()
      const res = await fetch(
        editTarget
          ? `${apiUrl}/product-categories/${editTarget.id}`
          : `${apiUrl}/product-categories`,
        {
          method: editTarget ? 'PATCH' : 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: name.trim() }),
        },
      )
      if (!res.ok) {
        setError(t('products.errorSaveCategory'))
        return
      }
      onSaved()
      onClose()
    } catch {
      setError(t('products.errorSaveCategory'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      scrollBehavior="inside"
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <ModalContent>
        {() => (
          <form
            className="flex min-h-0 flex-1 flex-col"
            onSubmit={(e) => {
              e.preventDefault()
              void handleSave()
            }}
          >
            <ModalHeader>
              {editTarget ? t('products.editCategory') : t('products.newCategory')}
            </ModalHeader>
            <ModalBody>
              <Input
                autoFocus
                label={t('products.categoryName')}
                placeholder={t('products.categoryPlaceholder')}
                value={name}
                onValueChange={setName}
                maxLength={80}
              />
              {error && <p className="text-sm text-danger">{error}</p>}
            </ModalBody>
            <ModalFooter>
              <Button variant="flat" onPress={onClose} isDisabled={saving}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" color="primary" isLoading={saving}>
                {t('common.save')}
              </Button>
            </ModalFooter>
          </form>
        )}
      </ModalContent>
    </Modal>
  )
}
